// ----- Phase 5.24 — StatusPipeline component ----- //
// ----- Phase status-stage-progress (TODO-083) — horizontal stepper + error + 7-stage ESG -----
//
// Visual representation of a submission's lifecycle pipeline. Pure
// presentation; takes a `stages` array and `currentStageKey` and
// renders either a vertical list (legacy) or a horizontal stepper
// with connector lines between
// stages, an error state, and a current-stage caption.
//
// The horizontal layout is collapsible at all widths: a summary row
// (actions, current-stage caption, toggle) stays visible while the stage
// list hides via the `hidden` attribute, keeping it out of the tab order
// and accessibility tree. Collapse is controlled via `expanded` /
// `onExpandedChange` or self-managed (collapsed by default).
//
// Stage states:
//   * `done`    — green check, full opacity, connector to the right is green
//   * `active`  — accent-coloured bullet, bold label, ring; connector to the right is muted
//   * `pending` — muted number circle, muted label, muted connector
//   * `error`   — red ✗, red label, red connector to the right (new in TODO-083)
//
// Shared by the Submission Details and Compiler status indicators.

import { Fragment, useId, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import type { PipelineStage as BackendPipelineStage } from '@/api/v2Client';

/**
 * TODO-083 widens the backend's `'pending' | 'active' | 'done'` union with
 * an `'error'` state synthesised client-side (e.g. validation failed,
 * ESG transmission errored). The component accepts the widened union
 * directly so consumers don't have to cast.
 */
export type PipelineStageState = BackendPipelineStage['state'] | 'error';

export interface PipelineStageDisplay {
  readonly key: string;
  readonly label: string;
  readonly state: PipelineStageState;
  readonly detail?: string;
}

/**
 * Backend key for the ESG "Submitted to Agency" stage (src/routes/submissions.ts).
 * The stage label reads as a completed transmission, but the backend also uses
 * this stage in its `pending` form — before any real dispatch exists — which
 * made the current-stage caption announce "Current: Submitted to Agency" for a
 * submission that was never transmitted (COV-ISS-001). Per D-077, nothing may
 * imply a submission reached the agency without a real transmit record.
 */
const AGENCY_STAGE_KEY = 'submitted-to-agency';

export interface StatusPipelineProps {
  readonly stages: readonly PipelineStageDisplay[];
  readonly currentStageKey: string;
  /** Visual orientation. Default 'horizontal' (TODO-083). */
  readonly actions?: ReactNode;
  readonly orientation?: 'vertical' | 'horizontal';
  /**
   * Horizontal mode collapses to a summary row (actions, current-stage
   * caption, toggle). Controlled pair; when omitted the component manages
   * its own state and starts collapsed.
   */
  readonly expanded?: boolean;
  readonly onExpandedChange?: (expanded: boolean) => void;
  /**
   * Explicit summary-row content shown when no stage matches
   * `currentStageKey` (e.g. status still loading or unavailable). The
   * caller owns the wording; the pipeline never invents a current stage.
   */
  readonly summary?: ReactNode;
}

const COLORS: Record<PipelineStageState, { bg: string; fg: string; label: string; connector: string }> = {
  done: {
    bg: '#15803D',
    fg: '#ffffff',
    label: '#111827',
    connector: '#15803D',
  },
  active: {
    bg: 'var(--accent, #2563EB)',
    fg: '#ffffff',
    label: '#111827',
    connector: '#E5E7EB',
  },
  pending: {
    bg: '#E5E7EB',
    fg: '#6B7280',
    label: '#6B7280',
    connector: '#E5E7EB',
  },
  error: {
    bg: '#B91C1C',
    fg: '#ffffff',
    label: '#B91C1C',
    connector: '#FCA5A5',
  },
};

export function StatusPipeline({
  stages,
  currentStageKey,
  orientation = 'horizontal',
  actions,
  summary,
  expanded: controlledExpanded,
  onExpandedChange,
}: StatusPipelineProps): ReactElement {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const expanded = controlledExpanded ?? uncontrolledExpanded;
  const stagesRegionId = useId();
  const toggleExpanded = () => {
    const next = !expanded;
    if (controlledExpanded === undefined) setUncontrolledExpanded(next);
    onExpandedChange?.(next);
  };
  const isHorizontal = orientation === 'horizontal';
  // Stage names identify a step, not proof that its checks passed. The state
  // and detail carry the validation outcome in both layouts.
  const displayStages = stages.map((stage) => {
    if (stage.key === 'validated') {
      return { ...stage, label: 'Submission validation' };
    }
    // COV-ISS-001 / D-077: a pending agency stage has NOT been transmitted, so
    // "Submitted to Agency" would be a false claim in the node and caption.
    // Keep the label neutral: unavailable snapshots also use pending stages.
    // Preserve their uncertainty detail instead of asserting no transmission.
    // An active dispatch, agency ack (done) or rejection (error) reflects a
    // real transmit/ack record and keeps the truthful "Submitted to Agency".
    if (stage.key === AGENCY_STAGE_KEY && stage.state === 'pending') {
      return {
        ...stage,
        label: 'Submission to agency',
        detail: stage.detail ?? 'Not submitted',
      };
    }
    return stage;
  });
  const currentStage = displayStages.find((s) => s.key === currentStageKey) ?? null;

  return (
    <div data-testid="status-pipeline-wrapper" className={isHorizontal ? 'compact-progress' : undefined}>
      {isHorizontal && (
        <div className="compact-progress-summary">
          {actions}
          {currentStage ? <div data-testid="status-pipeline-current-caption" style={{ fontSize: 13, color: '#374151' }}>
            Current: <strong>{currentStage.label}</strong>
            {currentStage.detail && <span> — {currentStage.detail}</span>}
          </div> : summary}
          <button type="button" className="compact-progress-toggle"
            aria-expanded={expanded} aria-controls={stagesRegionId} onClick={toggleExpanded}>
            {expanded ? 'Hide stages' : 'View all stages'}
          </button>
        </div>
      )}
      <div
        id={isHorizontal ? stagesRegionId : undefined}
        className={isHorizontal ? "compact-progress-stages" : undefined}
        data-expanded={expanded}
        hidden={isHorizontal && !expanded}
        data-testid="status-pipeline"
        role="list"
        aria-label="Submission pipeline progress"
        data-orientation={orientation}
        style={
          isHorizontal
            ? {
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                padding: '2px 0',
              }
            : {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 18,
                padding: '16px 0',
              }
        }
      >
        {displayStages.map((stage, idx) => {
          const isCurrent = stage.key === currentStageKey;
          const colors = COLORS[stage.state];
          const isLast = idx === stages.length - 1;
          const glyph =
            stage.state === 'done'
              ? '✓'
              : stage.state === 'error'
                ? '✗'
                : stage.state === 'active'
                  ? '●'
                  : idx + 1;

          // Compact horizontal tracker; the toggle reveals the full stage list.
          if (isHorizontal) {
            const emphasized = isCurrent;
            return (
              <Fragment key={stage.key}>
                <div
                  data-testid={`status-stage-${stage.key}`}
                  data-state={stage.state}
                  data-current={isCurrent ? 'true' : 'false'}
                  role="listitem"
                  aria-current={isCurrent ? 'step' : undefined}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 2, flex: '1 1 0', minWidth: 0,
                    color: stage.state === 'error' ? colors.label : emphasized ? '#1B3A6B' : colors.label,
                    fontSize: 12, fontWeight: emphasized ? 700 : 500,
                    textAlign: 'center', overflowWrap: 'normal', wordBreak: 'normal',
                  }}
                >
                  <span aria-hidden="true" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: '50%', fontSize: 11, fontWeight: 700, flexShrink: 0,
                    background: stage.state === 'error' ? colors.bg : emphasized ? '#1B3A6B' : stage.state === 'done' ? '#166534' : '#e5e7eb',
                    color: stage.state === 'error' || emphasized || stage.state === 'done' ? '#fff' : '#6b7280',
                  }}>
                    {glyph}
                  </span>
                  <span className="status-stage-label" style={{
                    padding: '1px 4px', borderRadius: 4,
                    background: emphasized ? (stage.state === 'error' ? '#fee2e2' : '#e4edfc') : 'transparent',
                  }}>{stage.label}</span>
                  {stage.detail && (
                    <span
                      data-testid={`status-stage-${stage.key}-detail`}
                      style={{
                        fontSize: 11,
                        opacity: 0.85,
                        color: '#6B7280',
                      }}
                    >
                      {stage.detail}
                    </span>
                  )}
                </div>
                {!isLast && (
                  <span
                    aria-hidden="true"
                    data-testid={`status-connector-${stage.key}`}
                    style={{
                      background: stage.state === 'done' ? '#166534' : stage.state === 'error' ? '#b91c1c' : '#94a3b8', height: 2, width: 10,
                      marginTop: 10, flexShrink: 0,
                    }}
                  >
                  </span>
                )}
              </Fragment>
            );
          }

          // ── Vertical (legacy): circle + label, no connectors ──────────────
          return (
            <div
              key={stage.key}
              data-testid={`status-stage-${stage.key}`}
              data-state={stage.state}
              data-current={isCurrent ? 'true' : 'false'}
              role="listitem"
              aria-current={isCurrent ? 'step' : undefined}
              style={verticalItemStyle()}
            >
              <div style={{ display: 'contents' }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: colors.bg,
                    color: colors.fg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    flexShrink: 0,
                    boxShadow: isCurrent ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
                  }}
                  aria-hidden="true"
                >
                  {glyph}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginLeft: 14 }}>
                <span
                  className="status-stage-label"
                  style={{
                    fontSize: 14,
                    fontWeight: stage.state === 'active' || isCurrent ? 600 : 500,
                    color: colors.label,
                    textAlign: 'left',
                  }}
                >
                  {stage.label}
                </span>
                {stage.detail && (
                  <span
                    data-testid={`status-stage-${stage.key}-detail`}
                    style={{ fontSize: 12, color: '#6B7280', textAlign: 'left' }}
                  >
                    {stage.detail}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}

function verticalItemStyle(): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  };
}
